/**
 * Dropping receipts in.
 *
 * The drop zone names the three Belegkreise instead of showing a cloud icon: choosing ER, AR or KA
 * is the one decision only the person can make, and everything else is read off the file.
 */
import { CheckCircle2, FileUp, Loader2, TriangleAlert } from "lucide-react";
import { useRef, useState } from "react";
import type { DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDocumentUpload } from "@/hooks/use-document-upload";
import { DOCUMENT_KINDS, type DocumentKind } from "@/lib/api/documents";
import { ownNames } from "@/lib/api/extract";
import { useAuth } from "@/lib/session/auth-context";
import { cn } from "@/lib/utils/cn";
import { useWorkspace } from "@/lib/session/workspace-context";

const ACCEPT = ".pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf";

export function UploadDialog({
  open,
  onOpenChange,
  defaultKind = "ER",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultKind?: DocumentKind;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<DocumentKind>(defaultKind);
  const [dragging, setDragging] = useState(false);

  const { upload, items, busy, reset } = useDocumentUpload({
    workspaceId: workspace?.id ?? "",
    userId: user?.id ?? "",
    ownVatId: workspace?.vatId,
    ownIban: workspace?.iban,
    ownNames: ownNames(workspace),
  });

  async function handleFiles(files: FileList | null) {
    const list = [...(files ?? [])];
    if (list.length === 0) return;
    const created = await upload(list, kind);
    if (created.length === 1) {
      toast.success(t("upload.doneOne"));
      onOpenChange(false);
      reset();
      navigate(`/documents/${created[0]}`);
    } else if (created.length > 1) {
      toast.success(t("upload.doneMany", { count: created.length }));
    }
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    void handleFiles(event.dataTransfer.files);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) {
          onOpenChange(next);
          if (!next) reset();
        }
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("upload.title")}</DialogTitle>
          <DialogDescription>{t("upload.subtitle")}</DialogDescription>
        </DialogHeader>

        <Tabs value={kind} onValueChange={(value) => setKind(value as DocumentKind)}>
          <TabsList className="grid w-full grid-cols-3">
            {DOCUMENT_KINDS.map((entry) => (
              <TabsTrigger key={entry} value={entry} data-testid={`upload-kind-${entry}`}>
                <span className="mr-1.5 ledger-id">{entry}</span>
                <span className="hidden sm:inline">{t(`kinds.${entry}`)}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <p className="-mt-2 text-xs text-muted-foreground">{t(`kindHints.${kind}`)}</p>

        <div
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            "flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center transition-colors",
            dragging ? "border-brand bg-brand/5" : "border-border",
          )}
          data-testid="dropzone"
        >
          <FileUp className="size-6 text-muted-foreground" />
          <p className="text-sm font-medium">{t("upload.drop")}</p>
          <p className="text-xs text-muted-foreground">{t("upload.formats")}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-1"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            data-testid="choose-files"
          >
            {t("upload.choose")}
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="sr-only"
            aria-label={t("upload.choose")}
            onChange={(event) => {
              void handleFiles(event.target.files);
              event.target.value = "";
            }}
          />
        </div>

        {items.length > 0 && (
          <ul className="space-y-1.5" data-testid="upload-progress">
            {items.map((item) => (
              <li key={item.name} className="flex items-center gap-2 text-sm">
                {item.state === "done" && !item.error && (
                  <CheckCircle2 className="size-4 shrink-0 text-success" />
                )}
                {item.state === "error" && (
                  <TriangleAlert className="size-4 shrink-0 text-destructive" />
                )}
                {(item.state === "uploading" || item.state === "extracting") && (
                  <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
                )}
                {item.state === "done" && item.error && (
                  <TriangleAlert className="size-4 shrink-0 text-warning" />
                )}
                <span className="truncate">{item.name}</span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {item.state === "uploading" && t("upload.uploading")}
                  {item.state === "extracting" && t("upload.extracting")}
                  {item.state === "done" && !item.error && t("upload.read")}
                  {item.state === "done" && item.error && t("upload.readFailed")}
                  {item.state === "error" && t("upload.failed")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
